import { observable } from '@legendapp/state';
import { enableReactTracking } from '@legendapp/state/config/enableReactTracking';
import { enableReactUse } from '@legendapp/state/config/enableReactUse';
import { batch } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';

// LocalStorage key for persisting user preferences (keep the same for backward compatibility)
export const SIDEBAR_OPEN_KEY = 'jaqln_sidebar_open_state';

// Helper to check if we're running on the client-side
const isClient =
  typeof window !== 'undefined' && typeof document !== 'undefined';

// Enable React integration with auto tracking
if (isClient) {
  enableReactTracking({
    auto: true,
  });

  // Enable React use for better hooks integration
  enableReactUse();
}

// Types
export type RequestStatus =
  | 'idle'
  | 'pending'
  | 'loading'
  | 'streaming'
  | 'completed'
  | 'success'
  | 'error';

// Sidebar state interface
export interface SidebarState {
  status: RequestStatus;
  error: Error | null;
  isTransitioning: boolean;
  isDeleting: boolean;
  isSidebarOpen: boolean;
}

// Initial state
const initialState: SidebarState = {
  status: 'idle',
  error: null,
  isTransitioning: false,
  isDeleting: false,
  isSidebarOpen: true,
};

// Create the observable store
export const sidebarState = observable<SidebarState>(initialState);

// Helper to update body classes based on state
const updateBodyClasses = (isOpen: boolean) => {
  if (!isClient) return;
  document.body.classList.toggle('sidebar-open', isOpen);
  document.body.classList.toggle('sidebar-closed', !isOpen);
  document.body.dataset.sidebarState = isOpen ? 'open' : 'closed'; // Keep dataset for potential CSS targeting
};

// Sidebar-specific actions
export const sidebarActions = {
  startLoading: () => {
    sidebarState.status.set('loading');
    sidebarState.error.set(null);
  },

  startTransition: () => {
    sidebarState.isTransitioning.set(true);
    sidebarState.status.set('pending');
  },

  completeTransition: () => {
    sidebarState.isTransitioning.set(false);
    sidebarState.status.set('idle');
  },

  setError: (error: Error) => {
    batch(() => {
      sidebarState.status.set('error');
      sidebarState.error.set(error);
      sidebarState.isTransitioning.set(false);
    });
  },

  startDelete: () => {
    batch(() => {
      sidebarState.isDeleting.set(true);
      sidebarState.status.set('pending');
    });
  },

  completeDelete: () => {
    batch(() => {
      sidebarState.isDeleting.set(false);
      sidebarState.status.set('idle');
    });
  },

  // Initialize sidebar based on user preference or screen size
  initializeSidebar: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Initializing sidebar...');

    let isSidebarOpen: boolean;
    try {
      const storedPreference = localStorage.getItem(SIDEBAR_OPEN_KEY);

      if (storedPreference !== null) {
        isSidebarOpen = storedPreference === 'true';
        logger.debug(
          '[sidebarStore] Initialized sidebar from stored preference',
          { isSidebarOpen }
        );
      } else {
        // Default behavior: open on desktop, closed on mobile
        isSidebarOpen = window.innerWidth >= 768;
        logger.debug(
          '[sidebarStore] Initialized sidebar based on screen size (no preference found)',
          { isSidebarOpen, isMobile: !isSidebarOpen }
        );
      }
    } catch (error) {
      logger.warn(
        '[sidebarStore] Failed to read localStorage during init, falling back to screen size',
        { error }
      );
      isSidebarOpen = window.innerWidth >= 768; // Default to open on desktop
    }

    sidebarState.isSidebarOpen.set(isSidebarOpen);
    updateBodyClasses(isSidebarOpen);
  },

  // Handle window resize
  handleResize: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Handling resize event...');

    const isCurrentlyMobile = window.innerWidth < 768;
    let shouldBeOpen: boolean;

    try {
      // Check if user has set a preference
      const storedPreference = localStorage.getItem(SIDEBAR_OPEN_KEY);

      if (storedPreference !== null) {
        // User has a preference, respect it unless they resize to mobile *while* open
        const preferredState = storedPreference === 'true';
        if (isCurrentlyMobile && preferredState) {
          // If user preferred open, but resized to mobile, force close *but don't save preference*
          shouldBeOpen = false;
          logger.debug(
            '[sidebarStore] Auto-closing sidebar on resize to mobile (preference was open)'
          );
        } else {
          // Otherwise, respect the stored preference
          shouldBeOpen = preferredState;
          logger.debug(
            '[sidebarStore] Respecting stored preference on resize',
            { shouldBeOpen }
          );
        }
      } else {
        // No preference set, base visibility on screen size
        shouldBeOpen = !isCurrentlyMobile; // Open on desktop, closed on mobile
        logger.debug(
          '[sidebarStore] Adjusting sidebar based on resize (no preference)',
          { shouldBeOpen }
        );
      }
    } catch (error) {
      logger.warn(
        '[sidebarStore] Failed to read localStorage during resize, falling back to screen size',
        { error }
      );
      shouldBeOpen = !isCurrentlyMobile; // Fallback: open on desktop
    }

    // Only update state if it needs to change
    if (sidebarState.isSidebarOpen.peek() !== shouldBeOpen) {
      sidebarState.isSidebarOpen.set(shouldBeOpen);
      updateBodyClasses(shouldBeOpen);
    }
  },

  // Toggle sidebar and save preference
  toggleSidebar: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Toggling sidebar...');

    const newState = !sidebarState.isSidebarOpen.peek(); // Use peek to avoid cycle
    sidebarState.isSidebarOpen.set(newState);
    updateBodyClasses(newState);

    // Save the user's explicit action as preference
    try {
      localStorage.setItem(SIDEBAR_OPEN_KEY, String(newState));
      logger.debug('[sidebarStore] Saved sidebar toggle preference', {
        newState,
      });
    } catch (error) {
      logger.warn('[sidebarStore] Failed to save sidebar toggle preference', {
        error,
      });
    }
  },

  // Open sidebar and save preference
  openSidebar: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Opening sidebar...');

    if (!sidebarState.isSidebarOpen.peek()) {
      sidebarState.isSidebarOpen.set(true);
      updateBodyClasses(true);

      // Save preference
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, 'true');
        logger.debug('[sidebarStore] Saved sidebar open preference');
      } catch (error) {
        logger.warn('[sidebarStore] Failed to save sidebar open preference', {
          error,
        });
      }
    }
  },

  // Close sidebar and save preference
  closeSidebar: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Closing sidebar...');

    if (sidebarState.isSidebarOpen.peek()) {
      sidebarState.isSidebarOpen.set(false);
      updateBodyClasses(false);

      // Save preference
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, 'false');
        logger.debug('[sidebarStore] Saved sidebar closed preference');
      } catch (error) {
        logger.warn('[sidebarStore] Failed to save sidebar closed preference', {
          error,
        });
      }
    }
  },

  // Clear sidebar preference (e.g., on logout)
  clearSidebarPreference: () => {
    if (!isClient) return;
    logger.debug('[sidebarStore] Clearing sidebar preference...');

    try {
      localStorage.removeItem(SIDEBAR_OPEN_KEY);
      // After clearing, re-initialize based on screen size
      sidebarActions.initializeSidebar();
    } catch (error) {
      logger.warn('[sidebarStore] Failed to clear sidebar preference', {
        error,
      });
    }
  },

  // Reset sidebar state completely (including preference)
  resetSidebarState: () => {
    logger.debug('[sidebarStore] Resetting sidebar state completely...');
    batch(() => {
      sidebarState.status.set('idle');
      sidebarState.error.set(null);
      sidebarState.isTransitioning.set(false);
      sidebarState.isDeleting.set(false);
      // Clear preference and re-initialize
      sidebarActions.clearSidebarPreference();
    });
  },
};

// Selectors for components to use
export const sidebarSelectors = {
  isSidebarOpen: () => sidebarState.isSidebarOpen.get(),
  isSidebarTransitioning: () => sidebarState.isTransitioning.get(),
  isSidebarDeleting: () => sidebarState.isDeleting.get(),
  getSidebarStatus: () => sidebarState.status.get(),
  getSidebarError: () => sidebarState.error.get(),
};
